import React, { createContext, useContext } from 'react';
import { View } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  interpolate,
  type SharedValue,
} from 'react-native-reanimated';
import { BottomTabBar, type BottomTabBarProps } from 'expo-router/js-tabs';

const TabBarVisibilityContext = createContext<SharedValue<number> | null>(null);

export function useTabBarProgress(): SharedValue<number> | null {
  return useContext(TabBarVisibilityContext);
}

export function TabBarVisibilityProvider({ children }: { children: React.ReactNode }) {
  const progress = useSharedValue(1);
  return (
    <TabBarVisibilityContext.Provider value={progress}>{children}</TabBarVisibilityContext.Provider>
  );
}

// Native only — on web, the AnimatedTabBar causes issues: mobile Safari has erratic scroll
// jumps during height animation, and Android mobile web sometimes fails to show the tab bar
// on load and interferes with bottom safe area insets. Web uses the default BottomTabBar.
export function AnimatedTabBar(props: BottomTabBarProps) {
  const progress = useTabBarProgress();
  const measuredHeight = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => {
    if (!progress || measuredHeight.value === 0) return {};
    return {
      height: interpolate(progress.value, [0, 1], [0, measuredHeight.value]),
      overflow: 'hidden' as const,
    };
  });

  return (
    <Animated.View style={animatedStyle}>
      <View
        onLayout={(e) => {
          if (measuredHeight.value === 0) {
            measuredHeight.value = e.nativeEvent.layout.height;
          }
        }}>
        <BottomTabBar {...props} />
      </View>
    </Animated.View>
  );
}
